import { useEffect, useMemo, useState } from "react";
import { getProfileByName, updateConnectionProfiles } from "../../services/integrationService";
import { Backdrop, Box, Button, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import "../../components/connectionType/connectionType.css";
import { connectionProfileType, connectionType, popupType } from "../../types/types"
import { GenericPopUp } from "../../components/popup/genericPopUp";
import { IntegrationActions } from "../../stateManagement/actions/integrationDashboardActions";
import { useDispatch } from "react-redux";
import _ from "lodash";

export function ConnectionConfiguration(props: {
  profileList : connectionProfileType[];
  connectionType: connectionType;
  activeProfile : connectionProfileType;
  decrement: ()=>void;
  increment: ()=>void;
}) {
  const dispatch = useDispatch()

  const defaultProfile: connectionProfileType = useMemo(()=>{
    return {
      name: "",
      type: "remote-stream",
      payload: "",
      lob: "",
      attributes: {
        provider: props.connectionType.providerType,
      },
    }
  },[props.connectionType.providerType]);

  const boxStyle = {
    display: "flex",
    gap: "1rem",
    flexDirection: "column",
    margin: "1rem 0"
  }

  const connectionProfiles : connectionProfileType[] = props.profileList ;
  const [openGenericModal, setOpenGenericModal] = useState(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState("");
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [activeProfile, handleChangeProfile] = useState<connectionProfileType>(defaultProfile);
  
  useEffect(() => {
    const activeProfile = props.activeProfile.attributes.provider === props.connectionType.providerType && props.activeProfile.name && props.activeProfile.name.length > 0 ? props.activeProfile : defaultProfile;
    const profileToSave = _.cloneDeep(activeProfile);
    if(props.connectionType.providerType ==='SOAP'){
      profileToSave.attributes.authHeader = JSON.stringify(profileToSave.attributes.authHeader)
    }
    handleChangeProfile(profileToSave)
  }, [defaultProfile, props.activeProfile, props.connectionType.providerType])
  
  const isNew = props.activeProfile.name.length===0;
  const openPopup = (message: string, type: popupType) => {
    setPopupType(type)
    setGenericMessage(message)
    setOpenGenericModal(true)
  }

  const updateProfile = async () => {
    const profileToSave = _.cloneDeep(activeProfile);
    if(profileToSave.name.trim().length === 0){
      openPopup(`Please enter a name for the connection profile.`, "Error")
      return;
    }
    setLoadingState(true);
    if(isNew){
      const profile = await getProfileByName(profileToSave.name);
      if(profile.status === 200){
        openPopup(`A profile with the same name already exists, please enter a different name.`, "Error")
        setLoadingState(false);
        return;
      }
    }
    if(props.connectionType.providerType ==='SOAP'){
      try{
        profileToSave.attributes.authHeader = JSON.parse(profileToSave.attributes.authHeader)
      }catch(err){
        openPopup(`Invalid Authorization Header, please enter a valid JSON.`, "Error")
        setLoadingState(false);
        return;
      }
    }
    const response = await updateConnectionProfiles(profileToSave);
    setLoadingState(false);
    if(response.status === 200 || response.status === 201){
      if (response.data.connected === false) {
        openPopup(`Invalid Connection details, please enter valid details and try again.`, "Error")
      } else if (response.data.connected === true) {
        dispatch(IntegrationActions.setProfileName(profileToSave.name))
        openPopup(`Connection Established Successfully!`, "Success")
      }
    } else {
      openPopup(`Error Occured while updating the connection profile.`, "Error")
    }
  };


  const onChangeMainValue = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const updatedProfile = connectionProfiles.find(
      (profile) => profile.name === event.target.value
    )
    if(updatedProfile) {
      dispatch(IntegrationActions.setProfileName(updatedProfile.name))
      handleChangeProfile(updatedProfile)
    }
    else{
      dispatch(IntegrationActions.setProfileName(""))
      handleChangeProfile(defaultProfile)
    }
  };
 
  const onChangeFieldValue = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.target.id === "name") {
      handleChangeProfile({...activeProfile, name:event.target.value});
    } else {
      handleChangeProfile({...activeProfile,attributes:{...activeProfile.attributes,[event.target.id]:event.target.value}});
    }
  };

  return (
    <>
      <GenericPopUp message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={() => setOpenGenericModal(c => {
          popupType === "Success" && props.increment();
          return false;
        })} />
      <Box sx={{ flexDirection: 'column' }}>
        <Typography variant="h6" marginBottom={3}>Enter Connection Details</Typography>
        <Box sx ={ boxStyle }>
          <TextField 
              autoComplete="true"
              label="Choose Connection Profile (if already exists)" 
              select
              value={props.activeProfile.name && props.activeProfile.name.length > 0 && props.activeProfile.attributes.provider === props.connectionType.providerType ? props.activeProfile.name : "--create-new--"} 
              onChange={onChangeMainValue}
              fullWidth>
              {<MenuItem key = "new" value="--create-new--">--create-new--</MenuItem>}
              {connectionProfiles.filter((option) => option.attributes.provider === props.connectionType.providerType).map((profile) => (
                  <MenuItem key={profile.name} value={profile.name}>
                    {profile.name}
                  </MenuItem>
              ))}
          </TextField>
          {<TextField 
              id="name" 
              label="Connection Name" 
              value={activeProfile.attributes.provider === props.connectionType.providerType ? activeProfile.name : ""} 
              onChange={onChangeFieldValue} 
              fullWidth 
              disabled={!isNew && activeProfile.attributes.provider === props.connectionType.providerType && connectionProfiles.map((profile)=> profile.name).includes(activeProfile.name)}></TextField>}
          {props.connectionType.attributes.map((attr) => (
            <TextField id={attr.name} label={attr.displayName} key = {attr.name} type={attr.type||'text'} onChange={onChangeFieldValue} value={(activeProfile.attributes.provider === props.connectionType.providerType && activeProfile?.attributes[attr.name]) || ""} fullWidth multiline={attr.name ==='body'} maxRows={attr.name ==='body' ? 4 : 0 }></TextField>
          ))}
        </Box>
        <Box width={"100%"} display="flex">
          <Button key="previous" className="navigation-btn bottom-bar-navigation-btn" onClick={props.decrement} variant="contained">Previous</Button>
          <Button key="next" className="navigation-btn bottom-bar-navigation-btn" onClick={updateProfile} variant="contained">Next</Button>
        </Box>
        {loadingState && (<Backdrop open={loadingState} style={{zIndex:1000}}>
          <CircularProgress color="inherit" />
        </Backdrop>)}
      </Box>
    </>
  );
}